import AnyCard from 'components/Card'
import LoadingCard from 'components/Card/LoadingCard'
import { ALL_FILTER, AnyContent, Article, ARTICLE_TYPE, ContentItem, GIF_TYPE } from 'interfaces'
import { STOCK_GIF_TYPE } from 'interfaces/Content/StockContentTypes'
import { ReactNode, useMemo } from 'react'
import { dedupeArticles, dedupeMixedArticles } from 'services/content/util'
import { sortByDateKeyAscending } from 'utils/sort/order'
import { FilterType } from 'shared'

const VIEWPORT_VISIBLE_CARDS = 10

export interface UseCarouselCardsParams {
  items: AnyContent[]
  filter: FilterType
  // internal load in progress (drives spinner animation on the loading cards)
  loading: boolean
  // load state pushed down from the parent
  externalLoading?: boolean
  sortByDate?: boolean
  skipFeatureChecks?: boolean
  onContentItemClick?(content: ContentItem): void
  onContentItemCompose?(content: ContentItem): void | boolean
}

const renderLoadingCards = (active: boolean, filter: FilterType): ReactNode[] => {
  const isGifFilter = filter === GIF_TYPE || filter === STOCK_GIF_TYPE
  const cards: ReactNode[] = []

  for (let i = 0; i < VIEWPORT_VISIBLE_CARDS; i++) {
    cards.push(<LoadingCard square active={active} key={`loading-${i}`} small mediaSpinner={isGifFilter} />)
  }

  return cards
}

// Builds the memoized array of card elements for a carousel. Returns a flat
// ReactNode[] (not a wrapper element) because CardList maps over its children
// directly to position each card — it must own every card as a direct child.
export const useCarouselCards = ({
  items,
  filter,
  loading,
  externalLoading,
  sortByDate,
  skipFeatureChecks,
  onContentItemClick,
  onContentItemCompose
}: UseCarouselCardsParams): ReactNode[] => {
  return useMemo(() => {
    let visibleItems = items
    const loadingCards = loading || externalLoading || visibleItems.length === 0
      ? renderLoadingCards(loading, filter)
      : []

    if (filter === ARTICLE_TYPE) {
      visibleItems = dedupeArticles(visibleItems as Article[])
    }

    if (filter === ALL_FILTER) {
      visibleItems = dedupeMixedArticles(visibleItems)
    }

    if (sortByDate) {
      // copy before sorting — never mutate the items array held in state
      visibleItems = [...visibleItems].sort(sortByDateKeyAscending('createdAt'))
    }

    const contentCards: ReactNode[] = visibleItems.map(item => (
      <AnyCard
        key={item.id}
        content={item as ContentItem}
        children={item as ContentItem}
        square
        small
        skipFeatureChecks={skipFeatureChecks}
        onClick={onContentItemClick}
        onCompose={onContentItemCompose}
      />
    ))

    return contentCards.concat(loadingCards)
  }, [items, filter, loading, externalLoading, sortByDate, skipFeatureChecks, onContentItemClick, onContentItemCompose])
}
